/**
 * Nutrition Agent
 * Uses Open Food Facts API to provide nutrition information for farm products
 */

interface NutritionData {
  product_name: string;
  brands?: string;
  nutriments?: {
    energy_100g?: number;
    fat_100g?: number;
    'saturated-fat_100g'?: number;
    carbohydrates_100g?: number;
    sugars_100g?: number;
    fiber_100g?: number;
    proteins_100g?: number;
    salt_100g?: number;
    sodium_100g?: number;
  };
  nutriscore_grade?: string;
  nova_group?: number;
  ingredients_text?: string;
  allergens?: string;
  labels?: string;
}

interface ProductSearchResult {
  code: string;
  product: NutritionData;
  status: number;
  status_verbose: string;
}

class NutritionAgent {
  private baseUrl: string;
  private userAgent: string;

  constructor() {
    this.baseUrl = process.env.OPEN_FOOD_FACTS_API_URL || 'https://world.openfoodfacts.org/api/v2';
    this.userAgent = 'SWFL-Local-Farms/1.0 (https://swfllocalfarms.com)';
  }

  /**
   * Search for product by barcode
   */
  async getProductByBarcode(barcode: string): Promise<NutritionData | null> {
    try {
      const response = await fetch(`${this.baseUrl}/product/${barcode}.json`, {
        headers: {
          'User-Agent': this.userAgent,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ProductSearchResult = await response.json();

      if (data.status === 1) {
        return data.product;
      }

      return null;
    } catch (error) {
      console.error('Error fetching product by barcode:', error);
      return null;
    }
  }

  /**
   * Search for products by name
   */
  async searchProducts(query: string, limit: number = 20): Promise<NutritionData[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/search?search_terms=${encodeURIComponent(query)}&page_size=${limit}&json=true`,
        {
          headers: {
            'User-Agent': this.userAgent,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.products || [];
    } catch (error) {
      console.error('Error searching products:', error);
      return [];
    }
  }

  /**
   * Get nutrition information for farm produce
   */
  async getProduceNutrition(produceName: string): Promise<NutritionData | null> {
    try {
      const products = await this.searchProducts(produceName, 1);
      return products.length > 0 ? products[0] : null;
    } catch (error) {
      console.error('Error getting produce nutrition:', error);
      return null;
    }
  }

  /**
   * Calculate health score based on nutrition data
   */
  calculateHealthScore(nutrition: NutritionData): number {
    let score = 100;

    const nutriments = nutrition.nutriments;
    if (!nutriments) return score;

    // Deduct points for unhealthy components
    if (nutriments.sugars_100g && nutriments.sugars_100g > 10) {
      score -= Math.min(20, (nutriments.sugars_100g - 10) * 2);
    }

    if (nutriments['saturated-fat_100g'] && nutriments['saturated-fat_100g'] > 5) {
      score -= Math.min(20, (nutriments['saturated-fat_100g'] - 5) * 3);
    }

    if (nutriments.sodium_100g && nutriments.sodium_100g > 0.5) {
      score -= Math.min(20, (nutriments.sodium_100g - 0.5) * 30);
    }

    // Add points for healthy components
    if (nutriments.fiber_100g && nutriments.fiber_100g > 3) {
      score += Math.min(10, nutriments.fiber_100g);
    }

    if (nutriments.proteins_100g && nutriments.proteins_100g > 5) {
      score += Math.min(10, nutriments.proteins_100g / 2);
    }

    return Math.max(0, Math.min(100, score));
  }

  /**
   * Format nutrition information for display
   */
  formatNutritionInfo(nutrition: NutritionData): string {
    if (!nutrition.nutriments) {
      return 'Nutrition information not available';
    }

    const nutriments = nutrition.nutriments;
    let info = `Nutrition per 100g:\n`;

    if (nutriments.energy_100g) {
      info += `Energy: ${nutriments.energy_100g} kcal\n`;
    }
    if (nutriments.proteins_100g !== undefined) {
      info += `Protein: ${nutriments.proteins_100g}g\n`;
    }
    if (nutriments.carbohydrates_100g !== undefined) {
      info += `Carbohydrates: ${nutriments.carbohydrates_100g}g\n`;
    }
    if (nutriments.sugars_100g !== undefined) {
      info += `  Sugars: ${nutriments.sugars_100g}g\n`;
    }
    if (nutriments.fat_100g !== undefined) {
      info += `Fat: ${nutriments.fat_100g}g\n`;
    }
    if (nutriments['saturated-fat_100g'] !== undefined) {
      info += `  Saturated: ${nutriments['saturated-fat_100g']}g\n`;
    }
    if (nutriments.fiber_100g !== undefined) {
      info += `Fiber: ${nutriments.fiber_100g}g\n`;
    }
    if (nutriments.salt_100g !== undefined) {
      info += `Salt: ${nutriments.salt_100g}g\n`;
    }

    if (nutrition.nutriscore_grade) {
      info += `\nNutri-Score: ${nutrition.nutriscore_grade.toUpperCase()}`;
    }

    return info;
  }
}

export default new NutritionAgent();
